import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import { downloadsAPI, ordersAPI } from '../services/api';
import '../App.css';
import './PurchaseComplete.css';

const PurchaseComplete = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [downloadInfo, setDownloadInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.pageYOffset > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadDownloadInfo = async () => {
      try {
        if (location.state?.downloadToken) {
          setDownloadInfo({
            token: location.state.downloadToken,
            password: location.state.downloadPassword,
            expiresAt: location.state.downloadExpiresAt
          });
          setLoading(false);
          return;
        }

        const savedOrder = sessionStorage.getItem('vague_last_order');
        if (savedOrder) {
          const orderData = JSON.parse(savedOrder);
          if (orderData.downloadToken) {
            setDownloadInfo({
              token: orderData.downloadToken,
              password: orderData.downloadPassword,
              expiresAt: orderData.downloadExpiresAt
            });
            setLoading(false);
            return;
          }
        }

        if (orderId) {
          const orderResponse = await ordersAPI.getByOrderId(orderId);
          if (orderResponse.success && orderResponse.data) {
            if (orderResponse.data.downloadToken) {
              setDownloadInfo({
                token: orderResponse.data.downloadToken,
                password: orderResponse.data.downloadPassword,
                expiresAt: orderResponse.data.downloadExpiresAt
              });
              setLoading(false);
              return;
            }
          }
        }

        if (savedOrder) {
          const orderData = JSON.parse(savedOrder);
          if (orderData.downloadToken) {
            await fetchDownloadInfo();
          } else {
            setError('Download information not available. The order may not have been processed yet.');
            setLoading(false);
          }
        } else {
          setError('Order information not found. Please check your order ID.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading download info:', err);
        setError('Failed to load download information');
        setLoading(false);
      }
    };

    loadDownloadInfo();
  }, [orderId, location.state, navigate]);

  useEffect(() => {
    if (downloadInfo?.expiresAt) {
      const updateTimer = () => {
        const now = new Date();
        const expires = new Date(downloadInfo.expiresAt);
        const diff = expires - now;

        if (diff <= 0) {
          setTimeRemaining('Expired');
          return;
        }

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        setTimeRemaining(`${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [downloadInfo]);

  const fetchDownloadInfo = async () => {
    try {
      const savedOrder = sessionStorage.getItem('vague_last_order');
      if (savedOrder) {
        const orderData = JSON.parse(savedOrder);
        if (orderData.downloadToken) {
          const response = await downloadsAPI.getInfo(orderData.downloadToken);
          if (response.success) {
            setDownloadInfo({
              token: orderData.downloadToken,
              password: response.data.password,
              expiresAt: response.data.expiresAt
            });
          } else {
            setError('Download link not found or expired');
          }
        }
      }
    } catch (err) {
      console.error('Error fetching download info:', err);
      setError('Failed to load download information');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (downloadInfo?.token) {
      window.location.href = downloadsAPI.download(downloadInfo.token);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  const menuItems = [
    { type: 'link', label: 'Home', to: '/' },
    { type: 'link', label: 'Vague', to: '/' },
    { type: 'link', label: 'Shop', to: '/shop' }
  ];

  if (loading) {
    return (
      <div className="purchase-complete-page">
        <ResponsiveNavbar
          menuItems={menuItems}
          logo="VELOURA"
          logoLink="/"
          scrolled={scrolled}
        />
        <div className="purchase-complete-container">
          <div className="loading-message">Loading download information...</div>
        </div>
      </div>
    );
  }
  if (error || !downloadInfo) {
    return (
      <div className="purchase-complete-page">
        <ResponsiveNavbar
          menuItems={menuItems}
          logo="VELOURA"
          logoLink="/"
          scrolled={scrolled}
        />
        <div className="purchase-complete-container">
          <div className="error-message">
            {error || 'Download information not available'}
          </div>
          <Link to="/shop" className="purchase-complete-button">
            Return to Shop
          </Link>

        </div>
      </div>
    );
  }
  const isExpired = timeRemaining === 'Expired' || new Date(downloadInfo.expiresAt) < new Date();
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="purchase-complete-page"
    >
      <ResponsiveNavbar
        menuItems={menuItems}
        logo="VELOURA"
        logoLink="/"
        scrolled={scrolled}
      />
      <div className="purchase-complete-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="purchase-complete-header"
        >
          <div className="purchase-complete-icon">✓</div>
          <h1 className="purchase-complete-title">Purchase Complete!</h1>
          <p className="purchase-complete-subtitle">
            Your order has been processed. Download your mod files below.
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="download-section"
        >
          <h2 className="download-section-title">Download Your Mods</h2>
          {isExpired ? (
            <div className="download-expired">
              <p>⚠️ This download link has expired.</p>
              <p>Please contact support if you need to download your files again.</p>
            </div>
          ) : (
            <>
              <div className="download-info">
                <div className="download-info-item">
                  <label>Time Remaining:</label>
                  <span className="time-remaining">{timeRemaining}</span>
                </div>
                <div className="download-info-item">
                  <label>Archive Password:</label>
                  <div className="password-display">
                    <code className="password-value">{downloadInfo.password}</code>
                    <button
                      className="copy-button"
                      onClick={() => copyToClipboard(downloadInfo.password)}
                      title="Copy password"
                    >
                      📋
                    </button>
                  </div>
                </div>
              </div>
              <button
                className="download-button"
                onClick={handleDownload}
                disabled={isExpired}
              >
                Download Mod Archive
              </button>

              <div className="download-instructions">
                <h3>Instructions:</h3>

                <ol>
                  <li>Click the download button above to download the archive</li>

                  <li>Extract the archive using the password shown above</li>

                  <li>Follow the installation instructions included in the archive</li>

                  <li>This download link will expire in 1 hour</li>

                </ol>

              </div>

            </>

          )}
        </motion.div>


        <div className="purchase-complete-actions">
          <Link to="/shop" className="purchase-complete-button secondary">
            Continue Shopping
          </Link>

          <Link to={`/confirmation/${orderId}`} className="purchase-complete-button">
            View Order Details
          </Link>

        </div>

      </div>

    </motion.div>

  );
};

export default PurchaseComplete;

